/**
 * Birtvy ↔ Library document sync — the canvas's write path to the `documents` table.
 *
 * Birtvy had NO write path at all: the only `/documents-api` call under birtvy/ was a GET, so editing a
 * document card on the canvas never reached its stored row. The row and the card then drifted, and because a
 * card could also exist with no row at all, selecting one and asking the agent to change it made the agent
 * match by TITLE and edit a different document (measured: 16 doc cards on canvas, 2 rows in the Library).
 *
 * The model this serves: an ORIGINAL is the row and owns the content; an INSTANCE is a canvas card that
 * references it by `documentId`. Many instances may point at one original. These helpers are the only place
 * the canvas writes an original, which is what keeps "one store" enforceable.
 *
 * Deliberately a separate module rather than a change to `useConversationDocuments` — that hook is shared with
 * the Library panel and carries its own React state; this is a plain fetch layer the canvas calls imperatively.
 */

/** A document row as the Library returns it. `content` is what the canvas paints from (read-only cache). */
export interface BirtvyLibraryDoc {
  id: string
  title?: string
  content?: string
}

const base = (conversationId: string) => `/documents-api/${encodeURIComponent(conversationId)}`

/** Every document row for this conversation. Returns null when the Library is unreachable — callers must treat
 *  null as "unknown", never as "empty", or a transient outage would look like every document being deleted. */
export async function fetchLibrary(conversationId: string): Promise<BirtvyLibraryDoc[] | null> {
  try {
    const res = await fetch(base(conversationId), { credentials: 'include' })
    if (!res.ok) return null
    const data = await res.json()
    const rows = Array.isArray(data) ? data : (data?.documents ?? [])
    return Array.isArray(rows) ? (rows as BirtvyLibraryDoc[]) : null
  } catch {
    return null
  }
}

/**
 * Write a document's body. Pass `title: null` so the BACKEND re-derives the title from the leading heading —
 * the canvas must not send its own derived title, or the first edit to a card silently renames the stored
 * document. The returned row carries the authoritative title; callers should take that back into their cache.
 */
export async function putDocument(
  conversationId: string,
  documentId: string,
  patch: { content: string; title?: string | null },
): Promise<BirtvyLibraryDoc | null> {
  try {
    const res = await fetch(`${base(conversationId)}/${encodeURIComponent(documentId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: patch.content, title: patch.title ?? null }),
    })
    if (!res.ok) return null
    return (await res.json()) as BirtvyLibraryDoc
  } catch {
    return null
  }
}

/**
 * Delete an original. Returns true when the row is gone.
 *
 * The canvas needs this because deleting a document CARD used to remove the card only: the row survived, so
 * the document was still in the Library, still in the agent's "Existing documents" block, and the agent would
 * happily write to it again — the user deleted something and watched it come back.
 */
export async function deleteDocument(conversationId: string, documentId: string): Promise<boolean> {
  try {
    const res = await fetch(`${base(conversationId)}/${encodeURIComponent(documentId)}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return res.ok
  } catch {
    return false
  }
}

/** Create an original and return its row (so the caller can place an instance referencing it). */
export async function createDocument(
  conversationId: string,
  doc: { title?: string; content: string },
): Promise<BirtvyLibraryDoc | null> {
  try {
    const res = await fetch(base(conversationId), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: doc.title ?? null, content: doc.content }),
    })
    if (!res.ok) return null
    return (await res.json()) as BirtvyLibraryDoc
  } catch {
    return null
  }
}
